import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, RefreshCw } from "lucide-react";
import { formatApiError } from "../../lib/api";
import {
  aiSuggestReply, aiSummarizeCase, aiClassifyCase, aiKbSuggestions, aiGetArtifacts,
  setPriority, setCategory,
} from "../../lib/supportWorkspace";

// Same 4-state confidence-badge vocabulary as BookingIntakeDraft.jsx's
// StatusPill (Confident/Uncertain/Missing/Edited): reused here as
// high/medium/low confidence plus an "unavailable" state, so agents who've
// seen AI-derived content elsewhere in the app recognize the pattern.
const CONFIDENCE_META = {
  high: { label: "High confidence", cls: "bg-emerald-100 text-emerald-800" },
  medium: { label: "Medium confidence", cls: "bg-amber-100 text-amber-800" },
  low: { label: "Low confidence: verify", cls: "bg-rose-100 text-rose-800" },
};

function ConfidencePill({ confidence }) {
  const meta = CONFIDENCE_META[confidence] || CONFIDENCE_META.low;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function UnavailableNote({ message }) {
  return (
    <p className="text-xs text-slate-500 italic" data-testid="ai-unavailable-note">
      {message || "AI suggestion unavailable. You can continue responding normally."}
    </p>
  );
}

function timestamp(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// caseId, agent capabilities, and a callback to push a suggested reply's
// text into the case's main reply textarea (never auto-sent: sending is
// still only possible through the existing reply form/send path).
export default function AiAssistancePanel({ caseId, caps, onUseReply }) {
  const [reply, setReply] = useState(null);
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyDraft, setReplyDraft] = useState("");
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [classification, setClassification] = useState(null);
  const [classifyLoading, setClassifyLoading] = useState(false);
  const [kb, setKb] = useState(null);

  // KB suggestions are a free deterministic lookup (see ai_service.
  // suggest_kb_articles): safe to load automatically on case open,
  // unlike the three LLM-backed actions which are always explicit.
  useEffect(() => {
    let cancelled = false;
    aiKbSuggestions(caseId).then((r) => { if (!cancelled) setKb(r); }).catch(() => {});
    aiGetArtifacts(caseId).then((r) => {
      if (cancelled) return;
      if (r.suggested_reply) { setReply({ available: true, ...r.suggested_reply.data, generated_at: r.suggested_reply.generated_at }); setReplyDraft(r.suggested_reply.data.suggested_reply || ""); }
      if (r.summary) setSummary({ available: true, ...r.summary.data, generated_at: r.summary.generated_at });
      if (r.classification) setClassification({ available: true, ...r.classification.data, generated_at: r.classification.generated_at });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [caseId]);

  async function runSuggestReply() {
    setReplyLoading(true);
    try {
      const r = await aiSuggestReply(caseId);
      setReply(r);
      setReplyDraft(r.available ? (r.suggested_reply || "") : "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReplyLoading(false);
    }
  }

  async function runSummarize() {
    setSummaryLoading(true);
    try {
      setSummary(await aiSummarizeCase(caseId));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSummaryLoading(false);
    }
  }

  async function runClassify() {
    setClassifyLoading(true);
    try {
      setClassification(await aiClassifyCase(caseId));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClassifyLoading(false);
    }
  }

  async function applyPriority() {
    if (!classification?.priority) return;
    try {
      await setPriority(caseId, classification.priority);
      toast.success("Priority updated from AI suggestion");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function applyCategory() {
    if (!classification?.category) return;
    try {
      await setCategory(caseId, classification.category, null);
      toast.success("Category updated from AI suggestion");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-5 mb-4" data-testid="ai-assistance-panel">
      <h2 className="text-sm font-semibold text-indigo-900 mb-1 flex items-center gap-1.5">
        <Sparkles className="w-4 h-4" aria-hidden="true" /> AI assistance
      </h2>
      <p className="text-xs text-indigo-800/70 mb-4">
        Suggestions only: nothing here is sent or applied until you choose to.
      </p>

      {/* --- Suggested reply --- */}
      <div className="mb-5 pb-5 border-b border-indigo-100">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-indigo-900">AI suggested reply</h3>
          <button type="button" data-testid="ai-suggest-reply-btn" onClick={runSuggestReply} disabled={replyLoading}
                  className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-indigo-300 bg-white hover:border-indigo-500 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${replyLoading ? "animate-spin" : ""}`} aria-hidden="true" />
            {reply ? "Regenerate" : "Suggest reply"}
          </button>
        </div>
        {reply && !reply.available && <UnavailableNote message={reply.message} />}
        {reply?.available && (
          <div data-testid="ai-suggested-reply-result">
            <div className="flex items-center gap-2 mb-1.5">
              <ConfidencePill confidence={reply.confidence} />
              {reply.generated_at && <span className="text-[11px] text-slate-500">Generated {timestamp(reply.generated_at)}</span>}
            </div>
            <textarea value={replyDraft} onChange={(e) => setReplyDraft(e.target.value)}
                      data-testid="ai-suggested-reply-text" rows={4}
                      className="w-full text-sm border border-indigo-200 rounded-lg px-3 py-2 bg-white" />
            {reply.kb_references?.length > 0 && (
              <p className="text-[11px] text-slate-500 mt-1">
                Based on: {reply.kb_references.map((k) => k.title).join(", ")}
              </p>
            )}
            <div className="flex gap-2 mt-2">
              <button type="button" data-testid="ai-use-reply-btn"
                      onClick={() => onUseReply(replyDraft)}
                      className="text-xs px-3 py-1.5 rounded-lg bg-[#1B4332] text-white font-medium hover:bg-[#143027]">
                Use this reply
              </button>
              <button type="button" data-testid="ai-discard-reply-btn" onClick={() => { setReply(null); setReplyDraft(""); }}
                      className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 hover:border-slate-500">
                Discard
              </button>
            </div>
          </div>
        )}
      </div>

      {/* --- Case summary --- */}
      <div className="mb-5 pb-5 border-b border-indigo-100">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-indigo-900">AI summary</h3>
          <button type="button" data-testid="ai-summarize-btn" onClick={runSummarize} disabled={summaryLoading}
                  className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-indigo-300 bg-white hover:border-indigo-500 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${summaryLoading ? "animate-spin" : ""}`} aria-hidden="true" />
            {summary ? "Regenerate" : "Summarize case"}
          </button>
        </div>
        {summary && !summary.available && <UnavailableNote message={summary.message} />}
        {summary?.available && (
          <div className="text-xs text-slate-700 space-y-1.5" data-testid="ai-summary-result">
            {summary.generated_at && <p className="text-[11px] text-slate-500">Generated {timestamp(summary.generated_at)}</p>}
            <p><strong>Issue:</strong> {summary.issue}</p>
            <p><strong>Customer goal:</strong> {summary.customer_goal}</p>
            {summary.important_facts?.length > 0 && (
              <p><strong>Important facts:</strong> {summary.important_facts.join("; ")}</p>
            )}
            {summary.actions_already_taken?.length > 0 && (
              <p><strong>Actions already taken:</strong> {summary.actions_already_taken.join("; ")}</p>
            )}
            {summary.unresolved_questions?.length > 0 && (
              <p><strong>Unresolved questions:</strong> {summary.unresolved_questions.join("; ")}</p>
            )}
            <p><strong>Current status:</strong> {summary.current_status}</p>
          </div>
        )}
      </div>

      {/* --- Classification / routing --- */}
      <div className="mb-5 pb-5 border-b border-indigo-100">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-indigo-900">Suggested classification</h3>
          <button type="button" data-testid="ai-classify-btn" onClick={runClassify} disabled={classifyLoading}
                  className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-indigo-300 bg-white hover:border-indigo-500 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${classifyLoading ? "animate-spin" : ""}`} aria-hidden="true" />
            {classification ? "Regenerate" : "Classify"}
          </button>
        </div>
        {classification && !classification.available && <UnavailableNote message={classification.message} />}
        {classification?.available && (
          <div className="text-xs text-slate-700 space-y-2" data-testid="ai-classification-result">
            <div className="flex items-center gap-2 flex-wrap">
              <ConfidencePill confidence={classification.confidence} />
              <span>Category: <strong>{classification.category || "-"}</strong></span>
              <span>Priority: <strong>{classification.priority || "-"}</strong></span>
              <span>Queue: <strong>{classification.suggested_queue || "-"}</strong></span>
            </div>
            {classification.escalation_recommended && (
              <p className="text-rose-700">Escalation suggested: {classification.escalation_reason || "see above"}</p>
            )}
            {caps.includes("priority") && (
              <div className="flex gap-2">
                {classification.category && (
                  <button type="button" data-testid="ai-apply-category-btn" onClick={applyCategory}
                          className="px-2.5 py-1 rounded-lg border border-slate-300 hover:border-emerald-600">
                    Apply category
                  </button>
                )}
                {classification.priority && (
                  <button type="button" data-testid="ai-apply-priority-btn" onClick={applyPriority}
                          className="px-2.5 py-1 rounded-lg border border-slate-300 hover:border-emerald-600">
                    Apply priority
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- KB suggestions --- */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-indigo-900 mb-2">Relevant knowledge base articles</h3>
        {kb && kb.articles?.length === 0 && (
          <p className="text-xs text-slate-500" data-testid="ai-kb-empty">No obviously relevant published articles found.</p>
        )}
        {kb?.articles?.length > 0 && (
          <ul className="text-xs text-slate-700 space-y-1" data-testid="ai-kb-list">
            {kb.articles.map((a) => (
              <li key={a.id} data-testid={`ai-kb-item-${a.id}`}>{a.title} <span className="text-slate-400">({a.category || "uncategorized"})</span></li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
